import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheService } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  LyftDispatchRide,
  LyftDispatchRideFareAdjustmentReason,
  LyftDispatchRideFareReviewStatusUpdate,
  LyftDispatchRideReceipt,
  LyftDispatchRideStatus,
  LyftDispatchRideSummary,
  LyftDispatchRideTracker,
  LyftDispatchRideTrackerDetail
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class LyftService extends UzurvRestService {
  readonly resourceUrl = 'lyftDispatchRides/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a list of dispatch ride statuses
   *
   * @param overrideCache
   * @permission lyft_dispatch_management
   */
  listLyftDispatchRideStatuses(): Observable<Array<LyftDispatchRideStatus>> {
    return this.get<Array<LyftDispatchRideStatus>>(`${this.resourceUrl}statuses/`).pipe(
      map(response => Deserialize(response, LyftDispatchRideStatus))
    );
  }

  /**
   * Returns a list of dispatch ride trackers with options
   * for searching and filtering by status
   *
   * @param pageNumber
   * @param pageSize
   * @param searchString
   * @param lyftDispatchRideStatusIds
   * @permission lyft_dispatch_management
   */
  listLyftDispatchRideTrackers(
    pageNumber: number,
    pageSize: number,
    searchString?: string,
    lyftDispatchRideStatusIds?: Array<number>
  ): Observable<Array<LyftDispatchRideTracker>> {
    return this.get<Array<LyftDispatchRideTracker>>(`${this.resourceUrl}trackers/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchString: searchString,
      lyftDispatchRideStatusIds: lyftDispatchRideStatusIds
    }).pipe(map(response => Deserialize(response, LyftDispatchRideTracker)));
  }

  /**
   * Gets a lyft dispatch ride by our reservation ID
   *
   * @param reservationId
   * @permission lyft_dispatch_management
   */
  findActiveByReservationId(reservationId: number): Observable<LyftDispatchRide> {
    return this.get<LyftDispatchRide>(this.resourceUrl, {
      reservationId: reservationId
    }).pipe(map(response => Deserialize(response, LyftDispatchRide)));
  }

  /**
   * Returns the detail object of a specified dispatch ride tracker by
   * ride request ID
   *
   * @param rideRequestId
   * @permission lyft_dispatch_management
   */
  getLyftDispatchRideTrackerDetail(rideRequestId: number): Observable<LyftDispatchRideTrackerDetail> {
    return this.get<LyftDispatchRideTrackerDetail>(`${this.resourceUrl}trackers/details/${rideRequestId}`).pipe(
      map(response => Deserialize(response, LyftDispatchRideTrackerDetail))
    );
  }

  /**
   * Gets billing information gathered from Lyft's system
   *
   * @param rideId
   * @permission lyft_dispatch_management
   */
  getRideReceipt(rideId: number): Observable<LyftDispatchRideReceipt> {
    return this.get<LyftDispatchRideReceipt>(`${this.resourceUrl}${rideId}/rideReceipt`).pipe(
      map(response => Deserialize(response, LyftDispatchRideReceipt))
    );
  }

  /**
   * Gets a summary of the ride by our reservation summary
   *
   * @param reservationId
   * @permission lyft_dispatch_management
   */
  getLyftDispatchRideSummary(reservationId: number): Observable<LyftDispatchRideSummary> {
    return this.get<LyftDispatchRideSummary>(`${this.resourceUrl}${reservationId}/summary`).pipe(
      map(response => Deserialize(response, LyftDispatchRideSummary))
    );
  }

  /**
   * Dispatches a ride to the Lyft API with an optional message for the driver
   *
   * @param rideRequestId
   * @param driverMessage
   * @permission lyft_dispatch_management
   */
  dispatchRideRequest(rideRequestId: number, driverMessage?: string): Observable<boolean> {
    return this.get<boolean>(`${this.resourceUrl}rideRequests/${rideRequestId}/dispatch`, {
      driverMessage: driverMessage
    }).pipe(map(() => true));
  }

  /**
   * Cancels a Lyft dispatch ride
   *
   * @param lyftDispatchRideId
   * @permission lyft_dispatch_management
   */
  cancelLyftDispatchRide(lyftDispatchRideId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${lyftDispatchRideId}?cancellationReasonName=Lyft+dispatcher+cancelled`).pipe(
      map(() => true)
    );
  }
}
